import type { EventSourceHandler } from './sse-client';
import { replaceTerms } from '../../language/lang';

/**
 * The event name of the notice the server sends a person, on their own connection, when a change
 * alters what they may do (AuthCacheInvalidationListener, SSEUserSecurityContextInvalidated). It is
 * pushed to the person rather than published on a topic, so it arrives by this name and not through
 * the hub, which routes only topic events (#398 C3).
 */
export const SECURITY_NOTICE_EVENT = 'security-context-invalidated';

/** The part of the SSE client a listener needs: listening by event name, and stopping. */
export interface EventNameSource {
  addEventListener(eventNames: string, event: EventSourceHandler): void;
  removeEventListener(eventNames: string, event: EventSourceHandler): void;
}

/**
 * Listens on the stream for this person's security notice and hands its payload to `onNotice`.
 * Nothing is asked of the server: the notice is sent to the person, so there is no topic to hold.
 * Returns the function that stops listening. Until #398 C3 the page held the company's group
 * security topic instead, which told every member of every change and anyone who asked.
 */
export function listenForSecurityNotice(
  source: EventNameSource,
  onNotice: (data: unknown) => void
): () => void {
  const handler: EventSourceHandler = (data) => onNotice(data);
  source.addEventListener(SECURITY_NOTICE_EVENT, handler);
  return () => source.removeEventListener(SECURITY_NOTICE_EVENT, handler);
}

/**
 * The text the person is shown for a notice: the server's reason with its term tags resolved, so it
 * reads in the current vocabulary. The server never puts a domain word in the reason, only tags
 * (CLAUDE.md, "Domain Words Belong to Terms"); shown unresolved, a tag reads as raw
 * `{{term ...}}` text in the toast (#398 C3 review 1). A notice with no reason gets a plain one.
 */
export function securityNoticeReason(data: unknown): string {
  const reason = (data as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' && reason.trim()
    ? replaceTerms(reason)
    : 'Security context updated';
}
